// SSR-compat: showdown is CJS, vite's SSR-Node loader doesn't extract named
// exports reliably. Default-import + destructure works in both SSR and browser.
import showdownPkg from "showdown";
import { useEffect, useRef, type FunctionComponent } from "react";

const { Converter } = showdownPkg;

const converter = new Converter({ openLinksInNewWindow: true });

/**
 * Welle 2.5 Bug 3 Fix (2026-05-21): isHtmlContent() Pre-Check entfernt.
 *
 * Showdown unterstützt HTML-Passthrough — bestehende HTML-Tags bleiben
 * unverändert, und Markdown-Patterns INNERHALB von HTML-Tags werden
 * trotzdem konvertiert. Beispiel PPI:
 *   Source: `<FONT SIZE=4>**Krim**</FONT>`
 *   Showdown-Output: `<p><FONT SIZE=4><strong>Krim</strong></FONT></p>`
 *
 * Der frühere Pre-Check hat HTML erkannt und Showdown übersprungen →
 * `**Krim**` blieb literal. Jetzt: converter.makeHtml() läuft immer.
 */

interface MarkdownViewerProps {
    source: string;
}

/**
 * MarkdownViewer renders slide-text content (HTML or markdown) into the
 * DOM via dangerouslySetInnerHTML, then walks the resulting subtree for
 * <video data-autoplay="1"> elements and registers a single
 * IntersectionObserver that drives play()/pause() based on viewport
 * visibility. Also calls .play() once the user has interacted with the
 * page so the muted-autoplay browser policy is satisfied.
 *
 * The observer is created once per slide-render and torn down on cleanup.
 * Native uploaded videos in slide texts now play when their slide scrolls
 * into view and pause when scrolled past — without the user clicking
 * anything. iframe-based YouTube/Vimeo embeds are NOT touched here; they
 * keep their click-to-play behavior.
 */
const MarkdownViewer: FunctionComponent<MarkdownViewerProps> = ({ source }) => {
    // Welle 2.5 Bug 3: converter.makeHtml() läuft immer (Showdown unterstützt
    // HTML-Passthrough + konvertiert Markdown innerhalb von HTML-Tags).
    const html = converter.makeHtml(source);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const root = containerRef.current;
        if (!root) return;
        const videos = Array.from(
            root.querySelectorAll<HTMLVideoElement>('video[data-autoplay="1"]'),
        );
        if (videos.length === 0) return;

        // Belt-and-braces: ensure the muted/loop/playsInline attrs are set
        // even when the HTML came from a path that didn't pre-set them.
        for (const v of videos) {
            v.muted = true;
            v.loop = true;
            v.playsInline = true;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    const video = entry.target as HTMLVideoElement;
                    if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
                        // Browser autoplay policy: ignore the rejection if it
                        // happens (no audio so policy *should* allow it).
                        video.play().catch(() => {});
                    } else {
                        video.pause();
                    }
                }
            },
            { threshold: [0, 0.4, 0.8] },
        );

        for (const v of videos) observer.observe(v);
        return () => observer.disconnect();
    }, [html]);

    return <div ref={containerRef} dangerouslySetInnerHTML={{ __html: html }} />;
};

export default MarkdownViewer;
